import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAssistantById, updateAssistant } from "../../../actions/assistants";
import { readWidgetTheme } from "../../../utils/widgetTheme";
import WidgetGlyph from "../../../Components/WidgetGlyph";
import {
  ChevronRightIcon,
  GlobeIcon,
  ClockIcon,
  SmileyIcon,
  PersonIcon,
  StarIcon,
  InfoIcon,
  KebabIcon,
  SendIcon,
  DesktopIcon,
  MobileIcon,
  PencilIcon,
  CodeIcon,
  BoltIcon,
} from "./icons";

const WELCOME_MAX = 200;

const purposeOptions = [
  {
    id: "answer",
    icon: <SmileyIcon />,
    title: "Answer questions",
    detail: "Provide helpful answers about your business.",
  },
  {
    id: "leads",
    icon: <PersonIcon />,
    title: "Capture leads",
    detail: "Collect visitor information and qualify interest.",
  },
  {
    id: "recommend",
    icon: <StarIcon />,
    title: "Recommend services / plans",
    detail: "Suggest the best options based on needs.",
  },
];

const features = [
  { icon: <PencilIcon />, title: "Edit anytime", detail: "Easily update your assistant as your business grows." },
  { icon: <CodeIcon />, title: "No coding required", detail: "Everything is set up for you automatically." },
  { icon: <BoltIcon />, title: "Ready in minutes", detail: "Go live in minutes and start engaging visitors." },
];

// status comes back as crawling | ready | failed; anything else falls through.
const STATUS_LABELS = {
  crawling: "Reading your website",
  ready: "Ready",
  failed: "Crawl failed",
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

export default function StepReview({ assistantId = "", onBack, onContinue }) {
  const dispatch = useDispatch();
  // Falls back to the store so a step back and forward doesn't lose the record.
  const storedAssistant = useSelector((state) => state.assistants.current);
  const id = assistantId || storedAssistant?.id || "";

  const [assistant, setAssistant] = useState(storedAssistant);
  const [loading, setLoading] = useState(Boolean(id));
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [assistantName, setAssistantName] = useState("Maya");
  const [roleSubtitle, setRoleSubtitle] = useState("AI receptionist for GoPointr");
  const [welcomeMessage, setWelcomeMessage] = useState("Hi! I'm Maya, your AI receptionist.\nHow can I help you today?");
  const [selectedPurposes, setSelectedPurposes] = useState(() => new Set(purposeOptions.map((p) => p.id)));
  // From the assistant's widget theme, so the preview shows the prompts real
  // visitors will be offered rather than a fixed pair.
  const [suggestions, setSuggestions] = useState([]);
  // The rest of that theme - the colour and the avatar a visitor will see.
  // Without it this preview was always Hostor indigo and a robot, whatever
  // the assistant had been given.
  const [theme, setTheme] = useState({});
  const [previewDevice, setPreviewDevice] = useState("desktop");

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    dispatch(getAssistantById(id)).then((result) => {
      if (cancelled) return;

      setLoading(false);

      if (!result?.success) {
        setLoadError(result?.message || "We couldn't load your assistant.");
        return;
      }

      const data = result.assistant;
      // welcomeMessage and suggestedPrompts moved inside widgetTheme; this
      // reads whichever shape the record is in.
      const theme = readWidgetTheme(data);

      setAssistant(data);
      setTheme(theme);
      setLoadError("");
      if (data?.name) setAssistantName(data.name);

      const welcome = theme.welcomeMessage || data?.welcomeMessage;
      if (welcome) setWelcomeMessage(welcome);

      if (Array.isArray(theme.suggestedPrompts)) {
        setSuggestions(theme.suggestedPrompts.filter(Boolean));
      }

      if (data?.allowedDomain) setRoleSubtitle(`AI receptionist for ${data.allowedDomain}`);
    });

    return () => {
      cancelled = true;
    };
  }, [dispatch, id]);

  const status = assistant?.status || "";

  // undefined leaves the stylesheet's own colour in place, which is what an
  // assistant with no theme should still look like.
  const brandStyle = theme.primaryColor
    ? { background: theme.primaryColor }
    : undefined;
  // The message avatar sits on the panel rather than on the header, so it
  // takes a tint of the colour rather than the colour itself.
  const glyphStyle = theme.primaryColor
    ? { background: theme.primaryColor, color: "#fff" }
    : undefined;

  /** Saves the edited name / welcome message before moving on. */
  const handleContinue = async () => {
    const trimmedName = assistantName.trim();
    const trimmedWelcome = welcomeMessage.trim();

    // Nothing to save against — let the user through rather than blocking them.
    if (!id) {
      onContinue();
      return;
    }

    if (!trimmedName) {
      setSaveError("Please give your assistant a name.");
      return;
    }
    if (!trimmedWelcome) {
      setSaveError("Please add a welcome message.");
      return;
    }

    const payload = {};
    if (trimmedName !== assistant?.name) payload.name = trimmedName;
    if (trimmedWelcome !== assistant?.welcomeMessage) payload.welcomeMessage = trimmedWelcome;

    // Untouched fields mean there's no PATCH worth sending.
    if (!Object.keys(payload).length) {
      onContinue();
      return;
    }

    setSaveError("");
    setSaving(true);

    const result = await dispatch(updateAssistant(id, payload));

    setSaving(false);

    if (result?.success) {
      if (result.assistant) setAssistant(result.assistant);
      onContinue();
      return;
    }

    setSaveError(result?.message || "We couldn't save your changes.");
  };

  const togglePurpose = (id) => {
    setSelectedPurposes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <>
      <h1 className="onboarding__title">Review your assistant</h1>
      <p className="onboarding__subtitle">
        Hostor prepared your AI assistant automatically. Review the essentials before going live.
      </p>

      <div className="onboarding__grid onboarding__grid--review">
        <section className="onboarding__card">
          {loading && <p className="review-record-loading">Loading your assistant…</p>}
          {loadError && <p className="auth-error">{loadError}</p>}

          {assistant && (
            <div className="review-record">
              <div className="review-record-head">
                <strong>{assistant.name}</strong>
                {status && (
                  <span className={`review-status review-status--${status}`}>
                    <span className="review-status-dot" />
                    {STATUS_LABELS[status] || status}
                  </span>
                )}
              </div>
              <ul className="review-record-list">
                <li>
                  <GlobeIcon />
                  <a href={assistant.sourceUrl} target="_blank" rel="noreferrer">
                    {assistant.sourceUrl}
                  </a>
                </li>
                {assistant.allowedDomain && (
                  <li>
                    <CodeIcon />
                    <span>{assistant.allowedDomain}</span>
                  </li>
                )}
                {formatDate(assistant.createdAt) && (
                  <li>
                    <ClockIcon />
                    <span>Created {formatDate(assistant.createdAt)}</span>
                  </li>
                )}
              </ul>
              {assistant.lastCrawlError && (
                <p className="review-record-error">{assistant.lastCrawlError}</p>
              )}
            </div>
          )}

          <div className="review-field-row">
            <label className="review-field">
              <span className="review-field-label">Assistant name</span>
              <input
                type="text"
                value={assistantName}
                onChange={(e) => setAssistantName(e.target.value)}
                className="review-input"
              />
            </label>
            <label className="review-field">
              <span className="review-field-label">Role / subtitle</span>
              <input
                type="text"
                value={roleSubtitle}
                onChange={(e) => setRoleSubtitle(e.target.value)}
                className="review-input"
              />
            </label>
          </div>

          <label className="review-field">
            <span className="review-field-label">Welcome message</span>
            <div className="review-textarea-wrap">
              <textarea
                className="review-textarea"
                rows={3}
                maxLength={WELCOME_MAX}
                value={welcomeMessage}
                onChange={(e) => setWelcomeMessage(e.target.value)}
              />
              <span className="review-char-count">
                {welcomeMessage.length} / {WELCOME_MAX}
              </span>
            </div>
          </label>

          <span className="review-field-label review-purpose-label">Main purpose</span>
          <ul className="review-purpose-list">
            {purposeOptions.map((option) => {
              const isSelected = selectedPurposes.has(option.id);
              return (
                <li key={option.id}>
                  <button
                    type="button"
                    className={`review-purpose-item${isSelected ? " is-selected" : ""}`}
                    onClick={() => togglePurpose(option.id)}
                    aria-pressed={isSelected}
                  >
                    <span className="review-purpose-check" aria-hidden="true">
                      {isSelected && (
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3">
                          <path d="M5 12.5l4.5 4.5L19 7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </span>
                    <span className="review-purpose-icon">{option.icon}</span>
                    <span className="review-purpose-text">
                      <strong>{option.title}</strong>
                      <span>{option.detail}</span>
                    </span>
                    <ChevronRightIcon />
                  </button>
                </li>
              );
            })}
          </ul>

          <p className="review-info">
            <InfoIcon />
            You can customize everything later.
          </p>

          {saveError && <p className="auth-error">{saveError}</p>}

          <div className="review-actions">
            <button
              type="button"
              className="onboarding__btn onboarding__btn--secondary"
              onClick={onBack}
              disabled={saving}
            >
              Customize more later
            </button>
            <button
              type="button"
              className="onboarding__btn onboarding__btn--primary"
              onClick={handleContinue}
              disabled={saving || loading}
            >
              {saving ? "Saving..." : "Looks good"}
              {!saving && <ChevronRightIcon />}
            </button>
          </div>
        </section>

        <section className="review-preview">
          <div className="review-preview-header">
            <span className="review-preview-title">Live preview</span>
            <div className="review-device-toggle">
              <button
                type="button"
                className={previewDevice === "desktop" ? "is-active" : ""}
                onClick={() => setPreviewDevice("desktop")}
              >
                <DesktopIcon />
                Desktop
              </button>
              <button
                type="button"
                className={previewDevice === "mobile" ? "is-active" : ""}
                onClick={() => setPreviewDevice("mobile")}
                aria-label="Mobile preview"
              >
                <MobileIcon />
              </button>
            </div>
          </div>

          <div className="review-preview-canvas">
            <div className={`review-chat${previewDevice === "mobile" ? " review-chat--mobile" : ""}`}>
              {/* The API's colour when it has one; the stylesheet's otherwise,
                  so a theme that sets nothing still looks finished. */}
              <div
                className="review-chat-header"
                style={brandStyle}
              >
                <span className="review-chat-avatar">
                  <WidgetGlyph theme={theme} size={20} />
                </span>
                <div className="review-chat-heading">
                  <strong>{assistantName || "Assistant"}</strong>
                  <span className="review-chat-status">
                    <span className="review-chat-status-dot" />
                    Online
                  </span>
                </div>
                <span className="review-chat-kebab">
                  <KebabIcon />
                </span>
              </div>

              <div className="review-chat-body">
                <div className="review-chat-message">
                  <span className="review-chat-message-avatar" style={glyphStyle}>
                    <WidgetGlyph theme={theme} size={16} />
                  </span>
                  <div>
                    <span className="review-chat-message-label">
                      {assistantName || "Assistant"} &bull; {roleSubtitle || "AI receptionist"}
                    </span>
                    <p className="review-chat-bubble">
                      {welcomeMessage.split("\n").map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          {i < welcomeMessage.split("\n").length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </p>
                  </div>
                </div>

                {suggestions.length > 0 && (
                  <div className="review-chat-suggestions">
                    {suggestions.map((suggestion) => (
                      <button
                        type="button"
                        key={suggestion}
                        // A long prompt takes its own row, as the two fixed
                        // ones here used to.
                        className={
                          suggestion.length > 18
                            ? "review-chat-suggestion--wide"
                            : undefined
                        }
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="review-chat-input">
                <input type="text" placeholder="Type your message..." disabled />
                <span className="review-chat-send" style={brandStyle}>
                  <SendIcon />
                </span>
              </div>

              <div className="review-chat-footer">
                Powered by <strong>hostor</strong>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="onboarding__features">
        {features.map((feature) => (
          <div className="onboarding__feature" key={feature.title}>
            <span className="onboarding__feature-icon">{feature.icon}</span>
            <div>
              <strong>{feature.title}</strong>
              <span>{feature.detail}</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
